import * as PIXI from 'pixi.js';
import { SoundManager } from '@/systems';

interface VolumeControlOptions {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  padding?: number;
}

/**
 * Volume control UI component that allows adjusting master volume
 */
export class VolumeControl {
  private container: PIXI.Container;
  private background: PIXI.Graphics;
  private masterSlider: PIXI.Container;
  private soundManager: SoundManager;
  private width: number;
  private height: number;
  // private padding: number; 
  private muteButton: PIXI.Container;
  // private volumeIcon: PIXI.Graphics | null = null;
  private isOpen = false;

  private screenWidth: number = 0;
  private screenHeight: number = 0;
  private overlay: PIXI.Graphics;

  // Store event handler functions for later cleanup
  private sliderEventHandlers: Map<string, (e: MouseEvent | PointerEvent) => void> = new Map();

  constructor(options: VolumeControlOptions = {}) {
    // Get options with defaults
    const {
      x = 10,
      y = 10,
      width = 300, 
      height = 80    } = options;

    this.width = width;
    this.height = height;
    // this.padding = padding;

    // Get the sound manager
    this.soundManager = SoundManager.getInstance();

    // Create the container
    this.container = new PIXI.Container();
    this.container.position.set(x, y);
    this.container.visible = false;
    this.container.interactive = true;
    this.container.eventMode = 'static';

    // Create close overlay (transparent background to catch outside clicks)
    this.overlay = new PIXI.Graphics();
    this.overlay.visible = false;
    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'default';
    this.container.addChild(this.overlay);

    // Create the background
    this.background = new PIXI.Graphics();
    this.background.interactive = true;
    this.background.eventMode = 'static';
    this.container.addChild(this.background);

    // Create volume icon/button
    this.muteButton = this.createVolumeButton();
    this.container.addChild(this.muteButton);

    // Create master volume slider
    this.masterSlider = this.createSlider('Volume', this.soundManager.getMasterVolume());
    this.container.addChild(this.masterSlider);

    // Load assets
    this.loadAssets().then(() => {
      this.updateVolumeIcon();
      this.draw();
    });

    // Draw the UI (initial draw might happen before assets)
    this.draw();
    this.setupEventListeners();
  }

  private volumeOnTexture: PIXI.Texture | null = null;
  private volumeOffTexture: PIXI.Texture | null = null;
  private volumeSprite: PIXI.Sprite | null = null;

  private async loadAssets(): Promise<void> {
    try {
      this.volumeOnTexture = await PIXI.Assets.load('assets/ui/b_volumeon.png');
      this.volumeOffTexture = await PIXI.Assets.load('assets/ui/b_volumeoff.png');
      this.updateVolumeIcon();
    } catch (e) {
      console.error("Failed to load volume icons", e);
    }
  }

  /**
   * Draw/redraw the control UI
   */
  private draw(): void {
    // Clear background
    this.background.clear();
    this.overlay.clear();

    if (this.isOpen) {
      // 1. Draw Overlay (invisible click catcher covering screen)
      const scale = this.container.scale.x || 1;
      const topLeft = this.container.toLocal(new PIXI.Point(0, 0));
      const safeW = this.screenWidth || 2000;
      const safeH = this.screenHeight || 2000;

      const width = safeW / scale;
      const height = safeH / scale;

      this.overlay.beginFill(0x000000, 0.01); // Minimal alpha to ensure interaction
      this.overlay.drawRect(topLeft.x, topLeft.y, width, height);
      this.overlay.endFill();
      this.overlay.visible = true;
      this.overlay.interactive = true;
      this.overlay.removeAllListeners();
      this.overlay.on('pointerdown', (e) => {
        e.stopPropagation();
        this.toggle();
      });

      // 2. Draw background for expanded panel (Themed: Dark Brown with Border)
      this.background.beginFill(0x2c1810, 0.9); // Dark brown
      this.background.lineStyle(2, 0x8b6914, 1); // Gold/Brown border
      this.background.drawRoundedRect(0, 0, this.width, this.height, 8);
      this.background.endFill();

      // Position and show slider - Horizontal Layout
      this.masterSlider.visible = true;
      // Position slider to the left of the mute button
      this.masterSlider.position.set(20, this.height / 2 - 10);

    } else {
      // Hide overlay
      this.overlay.visible = false;
      this.overlay.interactive = false;

      // Hide slider
      this.masterSlider.visible = false;
    }

    // Position volume icon/button
    // Centered vertically, aligned to right
    this.muteButton.position.set(this.width - 68, (this.height - 60) / 2);
  }

  /**
   * Creates the volume/mute button
   * @returns Container with the volume button
   */
  private createVolumeButton(): PIXI.Container {
    const container = new PIXI.Container();

    // Button size
    const size = 60;

    // Create button background for better hit area
    const buttonBg = new PIXI.Graphics();
    buttonBg.beginFill(0xFFFFFF, 0.01);
    buttonBg.drawRect(0, 0, size, size);
    buttonBg.endFill();
    container.addChild(buttonBg);

    // Create sprite placeholder
    this.volumeSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    this.volumeSprite.width = size;
    this.volumeSprite.height = size;
    this.volumeSprite.tint = 0x8b6914; // Fallback tint
    container.addChild(this.volumeSprite);

    // Create click handler function to avoid duplication
    const handleClick = (e: PIXI.FederatedPointerEvent) => {
      if (e) e.stopPropagation();

      if (!this.isOpen) {
        // Toggle panel if closed
        this.toggle();
      } else {
        // Toggle mute if panel is open
        this.soundManager.toggleMute();
        this.updateVolumeIcon();
      }
    };

    // Make button background interactive
    container.interactive = true;
    container.cursor = 'pointer';
    container.eventMode = 'static';
    container.on('pointerdown', handleClick);

    return container;
  }

  /**
   * Updates the volume icon based on current mute state
   */
  private updateVolumeIcon(): void {
    if (!this.volumeSprite) return;

    const isMuted = this.soundManager.isMuted();

    if (this.volumeOnTexture && this.volumeOffTexture) {
      this.volumeSprite.texture = isMuted ? this.volumeOffTexture : this.volumeOnTexture;
      this.volumeSprite.tint = 0xFFFFFF; // Remove tint when using texture
    } else {
      // Fallback if textures not loaded yet (or failed)
      this.volumeSprite.tint = isMuted ? 0xFF0000 : 0x00FF00;
    }
  }

  /**
   * Creates an interactive volume slider
   */
  private createSlider(label: string, initialValue: number): PIXI.Container {
    const container = new PIXI.Container();

    // Width logic: total width minus button space (approx 80) minus padding
    const sliderWidth = this.width - 100;

    // Create slider background (Track)
    const sliderBg = new PIXI.Graphics();
    sliderBg.beginFill(0x555555); // Dark grey track
    sliderBg.drawRoundedRect(0, 8, sliderWidth, 6, 3); // Centered vertically in container roughly
    sliderBg.endFill();
    sliderBg.interactive = true;
    sliderBg.cursor = 'pointer';
    sliderBg.eventMode = 'static';
    container.addChild(sliderBg);

    // Create slider fill (Active part)
    const sliderFill = new PIXI.Graphics();
    sliderFill.interactive = true;
    sliderFill.cursor = 'pointer';
    sliderFill.eventMode = 'static';
    container.addChild(sliderFill);

    // Create slider handle (Knob)
    const handle = new PIXI.Graphics();
    handle.beginFill(0xd4af37); // Gold knob
    handle.lineStyle(2, 0xffffff, 0.5);
    handle.drawCircle(0, 0, 10);
    handle.endFill();
    handle.position.set(sliderWidth * initialValue, 11); // Center y relative to track 8 + 3 = 11?
    container.addChild(handle);

    // Draw the fill based on handle position
    const updateSlider = () => {
      sliderFill.clear();
      sliderFill.beginFill(0x8b6914); // Gold/Brown fill
      sliderFill.drawRoundedRect(0, 8, handle.position.x, 6, 3);
      sliderFill.endFill();
    };

    // Initial draw
    updateSlider();

    // Make handle interactive
    handle.interactive = true;
    handle.cursor = 'pointer';
    handle.eventMode = 'static';

    // Enable dragging
    const dragging = { value: false };

    handle.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
      event.stopPropagation();
      dragging.value = true;
      handle.scale.set(1.2);
    });

    // Track pointer move for dragging
    const moveHandler = (event: MouseEvent) => {
      if (dragging.value) {
        // Get relative position within the slider
        // const bounds = container.getBounds();
        // Convert DOM event coordinates to PixiJS coordinates 
        // Note: Simple approximation here, usually need global to local
        // Using global x from event.
        // Better to use Pixi events if possible, but document level is safer for drag release outside

        // Re-calculate based on container global position
        // Since we don't have the event passed here as Federated, we rely on bounds
        // This can be tricky with scaling.
        // Alternative: Use container.toLocal 
        // But we are in a document handler.

        // Let's use the stored container x/scale if strictly 1:1, but safe way:
        // const relativeX = (event.clientX - bounds.x);
        // Correct for scale if needed, assuming global coords roughly map. 
        // Ideally should use Stage interaction manager.
        // For robustness in this simple implementation:

        // Fix: Pixi v7/8 handles events better. Let's rely on container-level move if possible? 
        // No, drag needs out-of-bounds.

        // Let's use a simplified approach since we are inside a class.
        // We know the slider width is strictly defined in local coords.
        // We need to map global mouse X to local slider X.

        // Let's rely on the handle's parent toLocal.
        const globalPoint = new PIXI.Point(event.clientX, event.clientY);
        const localPoint = container.toLocal(globalPoint);

        const newX = Math.max(0, Math.min(sliderWidth, localPoint.x));
        handle.position.x = newX;

        updateSlider();

        const volume = newX / sliderWidth;
        this.soundManager.setMasterVolume(volume);
      }
    };

    // Store the move handler for cleanup
    const moveHandlerId = `slider_move_${label}_${Date.now()}`;
    this.sliderEventHandlers.set(moveHandlerId, moveHandler);

    // Add the document event listener
    document.addEventListener('pointermove', moveHandler);

    // Handle pointer up to end dragging
    const endDrag = () => {
      if (dragging.value) {
        dragging.value = false;
        handle.scale.set(1);
      }
    };

    // Store event handlers for cleanup
    const handlerId = `slider_${label}_${Date.now()}`;
    this.sliderEventHandlers.set(handlerId, endDrag);

    // Listen for pointer up events on both container and document
    container.on('pointerup', endDrag);
    container.on('pointerupoutside', endDrag);

    // Add document-level event listeners for pointer up events
    document.addEventListener('pointerup', endDrag);
    document.addEventListener('pointercancel', endDrag);

    // Click on track
    const onTrackClick = (event: PIXI.FederatedPointerEvent) => {
      event.stopPropagation();
      const localPos = event.getLocalPosition(container); // Get local to container
      const newX = Math.max(0, Math.min(sliderWidth, localPos.x));

      handle.position.x = newX;
      updateSlider();

      const volume = newX / sliderWidth;
      this.soundManager.setMasterVolume(volume);
    };

    sliderBg.on('pointerdown', onTrackClick);
    sliderFill.on('pointerdown', onTrackClick);

    return container;
  }

  /**
   * Set up global event listeners
   */
  private setupEventListeners(): void {
    // Note: removed document 'mousedown' listener in favor of full-screen overlay (see draw method)

    // Update button icon when mute state changes
    let lastMuteState = this.soundManager.isMuted();

    // Create a periodic check for mute state changes
    setInterval(() => {
      const currentMuteState = this.soundManager.isMuted();
      if (lastMuteState !== currentMuteState) {
        // console.log(`Mute state changed from ${lastMuteState} to ${currentMuteState}`);
        this.updateVolumeIcon();
        lastMuteState = currentMuteState;
      }
    }, 200);
  }

  /**
   * Get the container for this control
   */
  public getContainer(): PIXI.Container {
    return this.container;
  }

  /**
   * Show the volume control
   */
  public show(): void {
    this.container.visible = true;
  }

  /**
   * Hide the volume control
   */
  public hide(): void {
    this.container.visible = false;
    this.isOpen = false;
    this.draw();
  }

  /**
   * Toggle between expanded and collapsed states
   */
  public toggle(): void {
    this.isOpen = !this.isOpen;
    this.draw();
  }

  /**
   * Update the position of the control
   */
  public updatePosition(x: number, y: number): void {
    this.container.position.set(x, y);
  }

  /**
   * Update screen dimensions for responsive layouts
   */
  public updateScreenSize(screenWidth: number, screenHeight: number): void {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Apply optional scaling if needed, or just position
    // For volume control, it's small enough, but let's prevent valid overlap
    const referenceWidth = 1280;
    const minScale = 0.7;
    const maxScale = 1.0;
    let scale = screenWidth / referenceWidth;
    scale = Math.max(minScale, Math.min(maxScale, scale));

    this.container.scale.set(scale);

    // Position in top-right corner
    const x = screenWidth - (this.width * scale) - 10;
    const y = 10;
    this.updatePosition(x, y);

    // Redraw to update overlay if open
    if (this.isOpen) {
      this.draw();
    }
  }

  /**
   * Clean up all resources and event listeners
   */
  public destroy(): void {
    // Clean up all document-level event handlers for sliders
    this.sliderEventHandlers.forEach((handler) => {
      document.removeEventListener('pointerup', handler);
      document.removeEventListener('pointercancel', handler);
      document.removeEventListener('pointermove', handler);
    });

    // Clear the handlers map
    this.sliderEventHandlers.clear();

    // Destroy all PIXI resources
    this.container.destroy({ children: true });
  }
}
